import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import { ProButton, IdentityPill, MUTED } from '../components/pro/proUi';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { proService } from '../services';
import { craftLabel } from '../utils/proCrafts';

/**
 * /pro/claim/{token} — the claim link from the outreach email (handoff §8). One
 * centred card whose state headline comes from the server: claimable, expired,
 * claimed, not_found; plus a logged-out state that resumes here after sign-in.
 */
export default function ProClaimPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await proService.claimPreview(token);
    setPreview(r.success ? r.data : { state: 'not_found' });
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const here = `/pro/claim/${token}`;
  const claim = async () => {
    setBusy(true); setError('');
    const r = await proService.claim(token);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not claim.'); return; }
    navigate('/pro/setup', { state: { claimed: true, waitingRequests: r.data?.convertedRequests || 0, name: r.data?.name } });
  };

  const state = preview?.state || 'not_found';
  const loggedOut = !user && state === 'claimable';
  const notFound = state === 'not_found';
  const waiting = preview?.waitingRequests || 0;

  let headline; let sub; let buttons;
  if (notFound) {
    headline = 'THIS LINK DOESN’T LEAD ANYWHERE';
    sub = 'The token is wrong, or the page it pointed at was removed. There is no business to show you.';
    buttons = <ProButton onClick={() => navigate('/pros')}>BROWSE PROS</ProButton>;
  } else if (loggedOut) {
    headline = 'LOG IN TO CLAIM';
    sub = 'The claim resumes right here after you sign in.';
    buttons = (
      <>
        <ProButton fill onClick={() => navigate('/login', { state: { from: here } })}>LOG IN TO CLAIM</ProButton>
        <ProButton onClick={() => navigate('/register', { state: { from: here } })}>CREATE ACCOUNT</ProButton>
      </>
    );
  } else if (state === 'claimable') {
    headline = 'THIS PAGE IS YOURS TO TAKE';
    sub = `You’ll land on your page editor. ${waiting} booking request${waiting === 1 ? ' is' : 's are'} waiting.`;
    buttons = <ProButton fill onClick={claim} disabled={busy}>{busy ? 'CLAIMING…' : 'CLAIM THIS PAGE'}</ProButton>;
  } else if (state === 'expired') {
    headline = 'THIS LINK HAS EXPIRED';
    sub = 'Claim links last seven days. Start again and we will send a fresh one.';
    buttons = <ProButton fill onClick={() => navigate(preview?.slug ? `/pro/${preview.slug}` : '/pros')}>START AGAIN</ProButton>;
  } else {
    headline = 'ALREADY CLAIMED';
    sub = 'Someone at this business finished the claim. Ask them for access, or write to us.';
    buttons = (
      <>
        <ProButton onClick={() => navigate(preview?.slug ? `/pro/${preview.slug}` : '/pros')}>VIEW THE PAGE</ProButton>
        <ProButton onClick={() => { window.location.href = 'mailto:hello@partyresume.com?subject=Pro%20page%20claim'; }}>CONTACT US</ProButton>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-inside-bg">
      <Navbar user={profile || user} walletBalance={walletBalance} onLogout={async () => { await logout(); navigate('/'); }} onLogoClick={() => navigate('/featured')} />
      <main className="mx-auto max-w-lg px-4 py-10 font-mono">
        {loading ? <LoadingSpinner /> : (
          <div className="border-2 border-black bg-white" data-testid={`claim-${loggedOut ? 'logged-out' : state}`}>
            <div className="border-b-2 border-black font-bold uppercase" style={{ padding: '14px 18px', fontSize: 13, letterSpacing: '0.1em' }}>{notFound ? 'CLAIM LINK' : 'CLAIM YOUR PAGE'}</div>
            {!notFound && (
              <div className="flex items-start justify-between gap-3 border-b-2 border-black" style={{ padding: 18 }}>
                <div className="min-w-0">
                  <div className="font-bold" style={{ fontSize: 18 }}>{preview.name}</div>
                  {preview.crafts?.length > 0 && <div className="mt-1 font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: MUTED }}>{preview.crafts.map(craftLabel).join(' · ')}</div>}
                  {preview.city && <div className="mt-0.5 uppercase" style={{ fontSize: 11, color: MUTED }}>{preview.city}</div>}
                </div>
                <IdentityPill listed />
              </div>
            )}
            <div className="flex flex-col gap-3" style={{ padding: 18 }}>
              <div className="font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.05em' }}>{headline}</div>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: MUTED }}>{sub}</p>
              {state === 'claimable' && user && preview?.viewerMatches === false && (
                <p style={{ fontSize: 12, color: MUTED }}>This claim was started from a different account. You can still finish it here; the page will belong to the account you&apos;re signed in with.</p>
              )}
              {error && <p className="font-bold uppercase" style={{ fontSize: 11, color: '#EF4444' }} role="alert">{error}</p>}
              <div className="mt-1 flex flex-col gap-2 sm:flex-row">{buttons}</div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
